//
// AuthServices.js
// ---------------
// This module handles all authentication related functionality using the Azure AD B2C service.
//
// See: https://github.com/AzureAD/microsoft-authentication-library-for-js/wiki/MSAL-Basics and
//      https://github.com/Azure-Samples/active-directory-b2c-javascript-msal-singlepageapp
//      https://github.com/Azure-Samples/ms-identity-javascript-v2/blob/master/app/authRedirect.js
//

import Vue from "vue";
import store from "../store/index";
import router from "../router/index";
import * as aCfg from "./AuthConfig";
import * as Msal from "@azure/msal-browser";
import Const from "./Constants";
import { checkOrGetGoodLink } from "@/utils/appointment-utils";

// Provide a global AUTHENTICATION object that the pages can access where they
// are insulated from the details.
export default class AuthService {
   constructor() {
      // --- AUTH REDIRECT ---
      // Create the main msalInst instance
      // configuration parameters are located at authConfig.js
      this.msalInst = new Msal.PublicClientApplication(aCfg.msalConfig);

      this.accountId = "";
      this.username = "";
      this.accessToken = null;
      this.isSpoofed = false;

      this.c = new Const();
      this.rtnApp = "";
      this.rtnNext = "";
      this.rtnInvite = "";

      // Handle the redirect flows
      this.msalInst
         .handleRedirectPromise()
         .then(async response => {
            if (response) {
               store.commit("updateAuthInProgress", true); // show wait spinner
               await this.handleResponse(response);
            }
         })
         .catch(error => {
            console.log(error);
            store.commit("updateAuthInProgress", false);

            // Check for forgot password error
            // Learn more about AAD error codes at https://docs.microsoft.com/en-us/azure/active-directory/develop/reference-aadsts-error-codes
            if (
               error.errorMessage &&
               error.errorMessage.indexOf("AADB2C90118") > -1
            ) {
               try {
                  // Password reset policy/authority
                  this.msalInst.loginRedirect(
                     aCfg.b2cPolicies.authorities.forgotPassword
                  );
               } catch (err) {
                  console.log(err);
               }
            }
         });

      // in case of page refresh
      this.selectAccount();
   }

   setAccount(account) {
      this.accountId = account.homeAccountId;
      this.username = account.username;
   }

   async selectAccount() {
      const currentAccounts = await this.msalInst.getAllAccounts();
      if (currentAccounts.length < 1) {
         return;
      } else if (currentAccounts.length > 1) {
         /**
          * Due to the way MSAL caches account objects, the auth response from initiating a user-flow
          * is cached as a new account, which results in more than one account in the cache. Here we make
          * sure we are selecting the account with homeAccountId that contains the sign-up/sign-in user-flow,
          * as this is the default flow the user initially signed-in with.
          */
         const accounts = currentAccounts.filter(
            account =>
               account.homeAccountId
                  .toUpperCase()
                  .includes(
                     aCfg.b2cPolicies.names.signUpSignIn.toUpperCase()
                  ) &&
               account.idTokenClaims.iss
                  .toUpperCase()
                  .includes(aCfg.msalConfig.auth.authorityDomain.toUpperCase()) &&
               account.idTokenClaims.aud === aCfg.msalConfig.auth.clientId
         );
         
         if (accounts.length > 1) {
            // localAccountId identifies the entity for which the token asserts information.
            if (
               accounts.every(
                  account =>
                     account.localAccountId === accounts[0].localAccountId
               )
            ) {
               // All accounts belong to the same user
               this.setAccount(accounts[0]);
            } else {
               // Multiple users detected. Logout all to be safe.
               this.logout();
            }
         } else if (accounts.length === 1) {
            this.setAccount(accounts[0]);
         }
      } else if (currentAccounts.length === 1) {
         this.setAccount(currentAccounts[0]);
      }
   }

   async handleResponse(response) {
      if (response.accessToken && response.accessToken !== "") {
         this.accessToken = response.accessToken;
      }
      if (
         response.account &&
         response.account.username &&
         response.account.username != ""
      ) {
         this.username = response.account.username;
         store.commit("updateUserEmail", this.username);
      }
      this.readInboundState(response.state);
      await this.handlePolicyChange(response);
      await this.selectAccount();
   }

   async handlePolicyChange(response) {
      // We need to reject id tokens that were not issued with the default sign-in policy.
      // "tfp" claim in the token tells us what policy is used.
      // To learn more about b2c tokens, visit https://docs.microsoft.com/en-us/azure/active-directory-b2c/tokens-overview
      const clmPolicy = "tfp";
      store.commit("updateAuthInProgress", true); // show wait spinner

      if (
         response.idTokenClaims[clmPolicy] ===
         aCfg.b2cPolicies.names.forgotPassword
      ) {
         console.log("BACK FROM Forgot Password");
         localStorage[this.c.STOR_UPD_PASSWORD] = "TRUE";
         Vue.prototype
            .$swal(
               "Success!",
               "Your password has been reset successfully.<br />Please sign-in with your new password.",
               "success"
            )
            .then(() => {
               store.commit("updateAuthInProgress", false);
               this.logout();
            });
      } else if (
         response.idTokenClaims[clmPolicy] ===
         aCfg.b2cPolicies.names.editProfile
      ) {
         Vue.nextTick(() => {
            localStorage[this.c.STOR_UPD_PROFILE] = "TRUE";
            Vue.prototype
               .$swal(
                  "Success!",
                  "Your profile has been updated.<br />Please log in again.",
                  "success"
               )
               .then(() => {
                  store.commit("updateAuthInProgress", false);
                  this.logout();
               });
         });
      } else if (
         response.idTokenClaims[clmPolicy] ===
            aCfg.b2cPolicies.names.signUpSignIn &&
         response.idTokenClaims["newUser"] &&
         response.idTokenClaims["newUser"] == true
      ) {
         await this.setIsInternalAdmin();
         Vue.nextTick(async () => {
            console.log("New User - Finishing sign up");
            await this.createAppUser(response)
               .then(async () => {
                  if ( store.getters.appointmentLink.toUpperCase().search("MD") == 0) {
                     //No error.  UpdateMD successfully.  Let reset the MD invitation link to blank.
                     store.commit('updateAppointmentLink', '')
                  }                  
                  await this.isLocallyActive(response);
                  console.log("New User - invoked isLocallyActive(response)");
                  console.log("New User - Finished successfully");
                  return;
               })
               .catch(async err => {
                  await this.handleError(
                     err.message === "error_link_missing"
                        ? err.message
                        : "create_user_error",
                     err
                  );
                  await this.logout();
                  return;
               });
         });
      } else {
         await this.setIsInternalAdmin();
         Vue.nextTick(async () => {
            console.log("Existing User - Now check if its locally an app user");
            await this.isLocallyActive(response);
            console.log("Existing User - invoked isLocallyActive(response)");
            console.log("Existing User - Finished successfully");
         });
      }
   }

   async handleError(errorCode = "default", err) {
      let message =
         "Something on our end went wrong while verifying your account.";
      let suppMessage =
         "Please try signing up again or contact support if the problem persists.";
      let header = "Oops!";
      let mdSuppMessage = "Please contact Marketing if the problem persists";

      if (errorCode === "sso_not_found") {
         message =
            "Your credentials are valid but your are not yet authorized to access EasyAppoint.";
      } else if (errorCode === "unknown_role") {
         message =
            "Something on our end went wrong while verifying your account.";
      } else if ( errorCode === "error_link_check" || 
                  errorCode === "wrong_md_update_link") {
         message = "An error occurred while validating link.";
      } else if (errorCode === "ext_error") {
         message =
            "Something on our end went wrong while setting up the appointment for your account.";
         suppMessage =
            "Please use an invitation link to sign in or contact Marketing if the problem persists.";
      } else if (errorCode === "error_link_missing") {
         message = "Appointment link is required.";
         suppMessage =
            "Please try again or click the invitation link found in your email.";
      } else if (errorCode === "create_user_error") {
         message = "There was an issue during sign up.";
         header = "Problem Creating Account";
         console.log("Failure to Create User? SSO Service Not Running?");
      } else if (errorCode === "get_user_error") {
         suppMessage =
            "Please try logging in again or contact support if the problem persists.";
      } else if (errorCode === "user_not_found") {
         header = "User Not Found";
         message = "An active account was not found for you.";
      } else if (errorCode === "not_in_same_org") {
         header = "Not In Same Hierarchy"
         message = "You cannot become Marketing Director since you have no hierarchy relationship with the replacing Market Director.";
         suppMessage = mdSuppMessage;
      } else if (errorCode === "unknown_md_update_error") {
         message = "There are some error while updating Marketing Director.";
         suppMessage = mdSuppMessage;
      } else if (errorCode === "cannot_assign_md_twice") {
         header = "You Are A Marketing Director In Another Hierarchy"
         message = "You cannot become Marketing Director since you are already a Marketing Director in another hierarchy.";
         suppMessage = mdSuppMessage;
      }
      console.log("Error Occurred: " + err);
      await Vue.prototype.$swal(header, `${message} ${suppMessage}`, "error");
   }

   async isUserInvitationAccepted() {
      let userAccepted = false;
      let userInviteId = store.getters.appointmentLink
         .toUpperCase()
         .substring(3);
      await Vue.prototype.$user
         .isUserInvitationAccpeted(userInviteId)
         .then(r => {
            userAccepted = r;
         })
         .catch(async err => {
            await Vue.prototype.$swal(
               "Oops!",
               "Something on our end went wrong while checking user invitation.  Please try again or contact support if the problem persists.",
               "error"
            );
            userAccepted = false;
         });
      return userAccepted;
   }

   parseUser(resp) {
      const result = {};
      console.log('inside ParseUser. Stat code = ' + resp.responseStatusCode)

      switch (resp.responseStatusCode) {
         case this.c.USERSTATUS_IN_SSO_INACTIVE:
         case this.c.USERSTATUS_IN_SSO_ONLY:
            result.canCreate = true;
            break;
         case this.c.USERSTATUS_SSO_NOT_FOUND:
            result.errorCode = "sso_not_found";
            break;
         case this.c.USERSTATUS_SIGNED_UP: {
            if (resp.appUser.userRoleCode == "VLECODE_ROLE_UNKNOWN") {
               result.errorCode = "unknown_role";
               break;
            }
            result.localUser = resp.appUser;
            console.log('result.localUser = ', result.localUser)
            console.log('result.canCreate = ', result.canCreate)
            break;
         }
         // default :
      }

      return result;
   }

   parseMDUpdate (r) {
      const result = {};
      switch (r.mdUpdateStatusCode) {
         case this.c.MDUPDATESTATUS_NOT_IN_SAME_ORG:
            result.errorCode = "not_in_same_org";
            break;
         case this.c.MDUPDATESTATUS_WRONG_MD_UPDATE_LINK:
            result.errorCode = "wrong_md_update_link";            
            break;
         case this.c.MDUPDATESTATUS_UNKOWN_MD_UPDATE_ERROR:
            result.errorCode = "unknown_md_update_error";
            break;
         case this.c.MDUPDATESTATUS_CANNOT_ASSIGN_MD_TWICE:
            result.errorCode = "cannot_assign_md_twice";
            break;
      }
      return result;
   }

   // This verifies the user created in both SSO & EasyAppt database, finish up the creation
   async isLocallyActive(response, mdEst) {
      let mdWasEstablished = (typeof mdEst === 'undefined') ? false : mdEst
      const user = await Vue.prototype.$user
         .getUserByEmail(this.username)
         .catch(async err => {
            console.log(
               "Failure to getUserByEmail(). SSO Service Not Running?",
               err
            );
            await this.handleError("get_user_error");
            await this.logout();
            return;
         });

      const result = this.parseUser(user);

      if (!result || result.errorCode) {
         await this.handleError(result.errorCode || "default");
         await this.logout();
         return;
      }

      let linkParm = ''
      if (store.getters.appointmentLink && store.getters.appointmentLink.length > 3) {
         linkParm = store.getters.appointmentLink.toUpperCase()
      }
      
      if (result.canCreate === true) {
         await this.createAppUser(response)
            .then(async () => await this.isLocallyActive(response))
            .catch(async err => {
               await this.handleError(
                  err.message === "error_link_missing" ? err.message : "create_user_error",
                  err
               );
               await this.logout();
               return;
            });
         return;
      } else if (linkParm != '') { 

         // Update User Functionality ------------------------------------------------
         if (linkParm.search("EXT") == 0) {
            let wasInvitationAccepted;
            if (store.getters.appointmentLink
               && store.getters.appointmentLink.toUpperCase().search("EXT") == 0) {
                  wasInvitationAccepted = await this.isUserInvitationAccepted();
            }
            if (wasInvitationAccepted === false) {
               await this.createAppUser(response)
                  .then(async () => {
                     // Link has been applied. We don't need it anymore
                     store.commit("updateAppointmentLink", '')
                     await this.isLocallyActive(response)
                  })
                  .catch(async err => {
                     await this.handleError(
                        err.message === "error_link_missing" ? err.message : "ext_error",
                        err
                     );
                     await this.logout();
                     return;
                  });
               return;
            }
         }

         // Update Marketing Director Functionality ------------------------------------
         else if (linkParm.search("MD") == 0) {
            let alink = this.rtnInvite
            if (alink == '' && store.getters.appointmentLink != null && store.getters.appointmentLink != '') {
               alink = store.getters.appointmentLink
            }
            let payload = {
               UserId: response.idTokenClaims.extension_SSOUserId,
               UserName: this.username,
               AppointmentLink: alink
            }

            await Vue.prototype.$user.updateMD(payload)
            .then(async(r) => {                        
               if (r != null) {
                  const result = this.parseMDUpdate(r);
                  if (!result || result.errorCode) {
                     await this.handleError(result.errorCode);
                     await this.logout();
                     return;
                  }

                  if (r.isUpdated == true) {
                     //Update appointment Link to blank if it is successful, so it won't display in Home page.                           
                     store.commit('updateAppointmentLink', '')
                  }
               }
               await this.isLocallyActive(response, true)   
            })
            .catch(async(err) => {
               console.log('Failure to Update MD', err)
               await Vue.prototype.$swal(
                  "Problem Updating Your User Record",
                  "Try logging in again or use the invitation link you were sent to login.  Please contact support if the problem persists",
                  "error"
               )
               await this.logout()
               store.commit('updateAuthInProgress',false)
            })
            return
         }

         // Update OLD-EZAppt User Functionality ---------------------------------------
         else if (linkParm.search("USR") == 0) {
            console.log('inside USR Processing')
            const usr = response.idTokenClaims;
            const now = this.toISOString(new Date());
            const payload = { 
               UserId: usr.extension_SSOUserId,
               UserName: store.getters.userEmail || this.username,
               ExternalId: response.uniqueId,
               CreateDt: now,
               CreatedById: usr.extension_SSOUserId,
               UpdateDt: now,
               UpdatedById: usr.extension_SSOUserId,
               IsInternal: false,
               IsActive: true,
               AppointmentLink: linkParm,
               FirstName: usr.given_name,
               LastName: usr.family_name
            };

            await Vue.prototype.$user.updateUSR(payload)
            .then(async () => {
               // Link has been applied. We don't need it anymore
               store.commit("updateAppointmentLink", '')
               await this.isLocallyActive(response)
            })
            .catch(async err => {
               await this.handleError(
                  err.message === "error_link_missing" ? err.message : "ext_error",
                  err
               );
               await this.logout();
               return
            });
            return
         }
      }

      if (!result.localUser || !result.localUser.isActive) {
         await this.handleError("user_not_found");
         await this.logout();
         return;
      }

      // Established user: navigate to original route
      if (mdWasEstablished == true) {
         result.localUser.userRoleCode = 'USRROLE_MD'
      }
      store.commit("updateAppUser", result.localUser);
      store.commit("updateUserClaims", response.idTokenClaims);
      store.commit("updateAuthInProgress", false);
      let nxt = this.rtnNext;
      if (nxt && nxt.length > 0) {
         console.log(
            `Current path is ${router.history.current.path} and re-establishing route to ${nxt}`
         );
         router.push({ path: nxt });
      }
   }

   // Tell B2C the user wants to edit their profile.
   async editProfile() {
      const editProfileRequest = aCfg.b2cPolicies.authorities.editProfile;
      editProfileRequest.loginHint = this.msalInst.getAccountByHomeId(
         this.accountId
      ).username;
      return this.msalInst.loginRedirect(editProfileRequest);
   }

   // The user wants to log in
   async login(nxtPg) {
      const logINRequest = aCfg.loginRequest;

      // Prefill their email address
      if (store.getters.userEmail != null) {
         logINRequest.loginHint = store.getters.userEmail;
      }
      logINRequest.state = this.buildOutboundState(nxtPg);
      return await this.msalInst.loginRedirect(logINRequest);
   }

   // The user wants to log out (all accounts)
   async logout() {
      // Removes all sessions, need to call AAD endpoint to do full logout
      store.commit("updateUserClaims", null);
      store.commit("updateUserEmail", null);
      this.accessToken = "";
      this.isSpoofed = false;

      // reset authentication status
      store.commit("updateAuthInProgress", false);

      // leaving account blank should eradicate ALL account caches
      const logoutRequest = {
         postLogoutRedirectUri: process.env.VUE_APP_B2C_REDIRECT_URL
      };
      await this.msalInst.logout(logoutRequest);
      return;
   }

   async alreadyLoggedOut() {
      // Removes all sessions, need to call AAD endpoint to do full logout
      store.commit("updateUserClaims", null);
      store.commit("updateUserEmail", null);
      store.commit("updateAuthInProgress", false);
      this.accessToken = "";
      window.location.replace(process.env.VUE_APP_B2C_REDIRECT_URL);
   }

   async getTokenRedirect(request) {
      request.account = this.msalInst.getAccountByHomeId(this.accountId);
      return this.msalInst
         .acquireTokenSilent(request)
         .then(response => {
            // In case the response from B2C server has an empty accessToken field
            // throw an error to initiate token acquisition
            if (!response.accessToken || response.accessToken === "") {
               throw new Msal.InteractionRequiredAuthError();
            } else {
               this.accessToken = response.accessToken;
            }
         })
         .catch(error => {
            console.log(
               "Silent token acquisition fails. Acquiring token. \n",
               error
            );
            if (error instanceof Msal.InteractionRequiredAuthError) {
               // fallback to interaction when silent call fails
               console.log(
                  "calling acquireTokenRedirect() after silent Failure"
               );
               return this.msalInst.acquireTokenRedirect(request);
            } else {
               console.log(error);
            }
         });
   }

   async Token() {
      if (!this.accessToken) {
         await this.selectAccount();
         await this.getTokenRedirect(aCfg.tokenRequest);
      }
      return this.accessToken;
   }

   isAuthenticated() {
      if (this.isSpoofed && this.accessToken) {
         return true;
      }

      const accounts = this.msalInst.getAllAccounts();
      return accounts && accounts.length > 0;
   }

   async updateLocalProfile() {
      let usr = store.getters.userClaims;
      var payload = {
         FirstName: usr.given_name,
         MiddleName: usr.extension_MiddleName,
         LastName: usr.family_name,
         PhoneNumber: usr.extension_PhoneNumber,
      };
      await Vue.prototype.$user.updateProfile(payload);
   }

   async createAppUser(resp) {
      console.log('inside AuthService.createAppUser()')
      var email = resp.account.username;
      let alink = store.getters.appointmentLink;

      if (!alink) {
         var userLinkDTO = await Vue.prototype.$user.getInvitationMultiLinkByEmail(email);
        // throw new Error("error_link_missing");
         alink = userLinkDTO.appointmentLink;
         if(alink.length == 0) {
            // no link, so prompt for one
            alink = await checkOrGetGoodLink().catch(err => {
               console.log('Create app error=', err);            
               throw new Error("error_link_missing");
            });
            
            if (!alink) {
               await this.logout()
               return
            }
         }
      }

      const usr = resp.idTokenClaims;
      const now = this.toISOString(new Date());
      const payload = { 
         UserId: usr.extension_SSOUserId,
         UserName: store.getters.userEmail || this.username,
         ExternalId: resp.uniqueId,
         CreateDt: now,
         CreatedById: usr.extension_SSOUserId,
         UpdateDt: now,
         UpdatedById: usr.extension_SSOUserId,
         IsInternal: false,
         IsActive: true,
         AppointmentLink: alink,
         FirstName: usr.given_name,
         LastName: usr.family_name
      };
      console.log("Sending this payload to createUser", payload);
      return await Vue.prototype.$user.createAppUser(payload);
   }

   toISOString(dat) {
      return (
         dat.getUTCFullYear() +
         "-" +
         this.pad(dat.getUTCMonth() + 1) +
         "-" +
         this.pad(dat.getUTCDate()) +
         "T" +
         this.pad(dat.getUTCHours()) +
         ":" +
         this.pad(dat.getUTCMinutes()) +
         ":" +
         this.pad(dat.getUTCSeconds()) +
         "." +
         (dat.getUTCMilliseconds() / 1000).toFixed(3).slice(2, 5) +
         "Z"
      );
   }

   pad(numbur) {
      if (numbur < 10) {
         return "0" + numbur;
      }
      return numbur;
   }

   buildOutboundState(nextPath) {
      // Tell B2C what app they want access to and their invitation ID if they are new
      let ste = "APP=" + store.getters.appCode;
      if (
         store.getters.appointmentLink != null &&
         store.getters.appointmentLink != ""
      ) {
         ste += ",ID=" + store.getters.appointmentLink;
      }
      if (nextPath && nextPath.length > 0) {
         ste += ",GO=" + nextPath;
      }
      return btoa(ste);
   }

   readInboundState(state) {
      let ste = atob(state);
      if (ste) {
         let parts = ste.split(",");

         this.rtnApp = "";
         this.rtnNext = "";
         this.rtnInvite = "";

         parts.forEach((item, index) => {
            if (item.length > 3) {
               if (item.substr(0, 3) == "GO=") {
                  this.rtnNext = item.substr(3);
               } else if (item.substr(0, 3) == "ID=") {
                  this.rtnInvite = item.substr(3);
                  console.log("rtnInvite=", this.rtnInvite);
                  /* console.log(
                     "store invite link=",
                     store.getters.appointmentLink
                  ); */
                  store.commit("updateAppointmentLink", item.substr(3))
               } else if (item.substr(0, 3) == "APP") {
                  this.rtnApp = item.substr(4);
               }
            }
         });
      }
   }

   async spoofEmail(email) {
      var params = new URLSearchParams({
         email
      });

      const response = await fetch(`${process.env.VUE_APP_BASE_API_URL}spoofUser?${params}`, {
         headers: {
            Authorization: `Bearer ${store.getters.internalAdmin.token}`
         }
      });

      if (response.status === 403) {
         await store.commit("setInternalAdmin", null);
      }

      if (!response.ok) {
         console.log(`spoof failed: ${await response.text()}`)
         return;
      }
      const token = await response.text();

      if (!token) {
         return;
      }

      this.accessToken = token;
      const user = await Vue.prototype.$user
         .getUserByEmail(email);

      if (!user.appUser) {
         console.log(`No app user found.`, user);
         this.accessToken = '';
         return;
      }

      this.isSpoofed = true;

      const result = this.parseUser(user);
      const claims = getJWTClaims(token);

      store.commit("updateAppUser", result.localUser);
      store.commit("updateUserClaims", claims);

      return true;
   }

   async setIsInternalAdmin() {
      try {
         const response = await fetch(`${process.env.VUE_APP_BASE_API_URL}spoofUser/me`, {
            headers: {
               Authorization: `Bearer ${this.accessToken}`
            }
         });

         const isInternalAdmin = response.ok;
         if (!isInternalAdmin) {
            await store.commit("setInternalAdmin", null);
            return;
         }

         const internalAdmin = {
            name: await response.text(),
            token: this.accessToken
         };
         await store.commit("setInternalAdmin", internalAdmin);
      } catch (e) {
         await store.commit("setInternalAdmin", null);
         return;
      }
   }
}

const getJWTClaims = (token) => {
   try {
      const [, payloadBase64] = token.split('.');
      const payload = JSON.parse(atob(payloadBase64));

      return payload;
   } catch (error) {
      throw new Error('Invalid JWT token');
   }
}
